import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { primaryColor } from "../../../../../utils/comon";
import apiServices from "../../../../../services/apiService";
import encryptedDataService from "../../../../../services/encryptedDataService";

const VisitorPass = () => {
    const [searchParams] = useSearchParams();

    const [passDetails, setPassDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const rawCardNumber = searchParams.get("m");
    // Restore any '+' characters that URL parsing converted to spaces
    const cardNumber = rawCardNumber ? rawCardNumber.replace(/ /g, '+') : null;

    useEffect(() => {
        if (cardNumber) {
            fetchPassDetails(cardNumber);
        } else {
            setError("No visitor card number found in URL.");
        }
    }, [cardNumber]);

    const fetchPassDetails = async (cardNo) => {
        setLoading(true);
        try {
            const response = await apiServices({
                method: "GET",
                url: `/Residential/ResVisitor/getVisitorByCardNo?cardNo=${encodeURIComponent(cardNo)}`,
            });

            if (response?.success || response?.isSuccess) {
                // Map the API response exactly as WalkInVisitorPass
                const dataArray = response?.data?.data || response?.data || [];
                const data = Array.isArray(dataArray) ? dataArray[0] : dataArray;

                if (data) {
                    const mappedData = {
                        full_name: data.visitorName || "",
                        coming_from: data.location || data.address || "Walk-in",
                        mobile: data.visitorMobileNumber || "",
                        qr_payload: data.qrNumber1 || `visitor-${data.visitorId}`,
                        valid_from: data.createdDate || new Date().toISOString(),
                        joiners: data.additionalCount || 0,
                        purpose_text: data.purposeOfVisit || "",
                        siteName: data.siteName,
                        hostName: data.hostName,
                        unitName: data.unitName || data.unitId,
                        visit_status: data.status,
                        visitorType: data.visitorType || "",
                    };
                    setPassDetails(mappedData);
                } else {
                    setError("Visitor not found.");
                }
            } else {
                setError(response?.message || "Failed to load pass details.");
            }
        } catch (err) {
            console.error("Error fetching pass details:", err);
            setError("An error occurred while loading the pass.");
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (iso) => {
        if (!iso) return "—";
        return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    };

    const formatTime = (iso) => {
        if (!iso) return "—";
        return new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
    };

    if (loading) {
        return <div className="flex-1 flex items-center justify-center p-4">Loading pass details...</div>;
    }

    return (
        <div className="flex-1 flex flex-col items-center justify-start bg-gray-300 p-4 overflow-y-auto scrollbar-hide w-full h-full min-h-[90vh]">
            <div className="w-full max-w-[360px] flex flex-col relative mt-4 mb-8">
                <div className="bg-[#0A0A0B] rounded-t-[1.5rem] p-6 relative overflow-hidden">
                    <div className="flex justify-between items-center mb-4">
                        <h1 className="text-2xl font-black tracking-tight cursor-pointer"
                            onClick={() => window.open("https://myhello.in/", "_blank")}
                            style={{ color: primaryColor() }}>
                            Hello!
                        </h1>
                        <span className="text-[8px] font-bold text-white border border-white/40 px-3 py-1 rounded-full uppercase tracking-widest">
                            {passDetails?.visitorType} Visitor
                        </span>
                    </div>

                    <h2 className="text-white text-xl font-bold leading-none tracking-tight">
                        {passDetails?.full_name || "—"}
                    </h2>

                    <div className="flex items-center gap-3 mt-4 text-white text-xs font-bold">
                        <span>{passDetails?.coming_from || "—"}</span>
                        <span className="opacity-40">|</span>
                        <span>{passDetails?.mobile || "—"}</span>
                    </div>
                </div>

                {/* NOTCH DIVIDER */}
                <div className="relative bg-[#F8FAFC]">
                    {/* Left Notch */}
                    <div className="absolute left-[-12px] top-[-12px] w-6 h-6 bg-gray-300 rounded-full z-10" />
                    {/* Right Notch */}
                    <div className="absolute right-[-12px] top-[-12px] w-6 h-6 bg-gray-300 rounded-full z-10" />
                    {/* Dashed Line */}
                    <div className="absolute top-0 left-8 right-8 border-t-2 border-dashed border-gray-200" />
                </div>

                {/* MIDDLE SECTION (Off-white) */}
                <div className="bg-[#F8FAFC] px-6 pb-6 flex flex-col items-center">
                    <p className="text-[11px] font-bold text-[#75777F] tracking-[0.5px] p-2 mt-2">
                        Scan to Verify Entry
                    </p>

                    {/* QR Container */}
                    <div className="bg-white p-6 rounded-3xl relative shadow-sm mt-2">
                        {/* Corner Markers */}
                        <div className="absolute top-4 left-4 w-6 h-6 border-t-[3px] border-l-[3px] rounded-tl-md" style={{ borderColor: "#D4AF37" }} />
                        <div className="absolute top-4 right-4 w-6 h-6 border-t-[3px] border-r-[3px] rounded-tr-md" style={{ borderColor: "#D4AF37" }} />
                        <div className="absolute bottom-4 left-4 w-6 h-6 border-b-[3px] border-l-[3px] rounded-bl-md" style={{ borderColor: "#D4AF37" }} />
                        <div className="absolute bottom-4 right-4 w-6 h-6 border-b-[3px] border-r-[3px] rounded-br-md" style={{ borderColor: "#D4AF37" }} />

                        <QRCodeSVG
                            value={passDetails?.qr_payload || "visitor-pass"}
                            size={200}
                            bgColor="#ffffff"
                            fgColor="#000000"
                            level="M"
                            includeMargin={false}
                        />
                    </div>

                    {/* Details Grid */}
                    <div className="w-full mt-6 grid grid-cols-3 gap-y-4">
                        <div className="border-r border-gray-100 pr-2">
                            <p className="text-[10px] font-bold text-gray-400 uppercase">Date</p>
                            <p className="text-xs font-bold text-[#1E293B] mt-1">
                                {formatDate(passDetails?.valid_from)}
                            </p>
                        </div>
                        <div className="border-r border-gray-100 px-2 text-center">
                            <p className="text-[10px] font-bold text-gray-400 uppercase">Time</p>
                            <p className="text-[13px] font-bold text-[#1E293B] mt-1">
                                {formatTime(passDetails?.valid_from)}
                            </p>
                        </div>
                        <div className="pl-2 text-right">
                            <p className="text-[10px] font-bold text-gray-400 uppercase">Joiners</p>
                            <p className="text-[13px] font-bold text-[#1E293B] mt-1">
                                {passDetails?.joiners || 0}
                            </p>
                        </div>

                        <div className="col-span-2">
                            <p className="text-[10px] font-bold text-gray-400 uppercase">Purpose of Visit</p>
                            <p className="text-[13px] font-bold text-[#1E293B] mt-1">
                                {passDetails?.purpose_text || "—"}
                            </p>
                        </div>
                        <div className="text-right">
                            <p className="text-[10px] font-bold text-gray-400 uppercase">Coming From</p>
                            <p className="text-[13px] font-bold text-[#1E293B] mt-1">
                                {passDetails?.coming_from || "—"}
                            </p>
                        </div>
                    </div>

                    {/* VISITING CARD (Black) */}
                    <div className="w-full mt-6 bg-[#0A0A0B] rounded-3xl p-5 relative overflow-hidden">
                        <div className="flex justify-between items-start">
                            <div>
                                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Visiting</p>
                                <h3 className="text-white text-xs font-bold mt-2 leading-tight">
                                    {passDetails?.hostName || "—"}, {passDetails?.unitName || "—"}
                                </h3>
                                <p className="text-gray-400 text-xs mt-1">
                                    {passDetails?.siteName || "—"}
                                </p>
                            </div>
                            <div className="flex items-center gap-1.5 bg-[#0D1B16] border border-[#143228] px-3 py-1.5 rounded-full">
                                <div className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
                                <span className="text-[10px] font-black text-[#10B981] tracking-widest">{passDetails?.visit_status}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* BOTTOM SECTION (Off-white) */}
                <div className="bg-[#F8FAFC] rounded-b-[2.5rem] pb-6 cursor-pointer"
                    onClick={() => window.open("https://zentechinfo.com/", "_blank")}
                >
                    <div className="flex justify-center items-center mt-2">
                        <img
                            src="/zentech.svg"
                            alt="Zentech"
                            className="h-8 opacity-100"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                            }}
                        />
                        <span style={{ display: 'none' }} className="font-bold text-gray-500">Zentech Info Solutions</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VisitorPass;
